import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Mail, Lock, Phone, Building2, ArrowRight, FileSignature, Briefcase } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
type AccountType = 'buyer' | 'owner' | 'company' | 'poa';
type BuyerKind = 'individual' | 'corporate';
export const Register = () => {
  const navigate = useNavigate();
  const {
    login
  } = useAuth();
  const [accountType, setAccountType] = useState<AccountType>('buyer');
  const [buyerKind, setBuyerKind] = useState<BuyerKind>('individual');
  const handleRegister = (e: React.FormEvent) => {
    e.preventDefault();
    login();
    if (accountType === 'buyer') navigate('/dashboard/buyer');else navigate('/dashboard/seller');
  };
  const tabs: Array<{
    id: AccountType;
    label: string;
  }> = [{
    id: 'buyer',
    label: 'Buyer'
  }, {
    id: 'owner',
    label: 'Property Owner'
  }, {
    id: 'company',
    label: 'Real Estate Co.'
  }, {
    id: 'poa',
    label: 'Authorized (POA)'
  }];
  return <div className="min-h-screen flex bg-neutral-light" data-id="element-1110">
      <div className="w-full max-w-2xl mx-auto p-6 md:p-12 flex flex-col justify-center" data-id="element-1111">
        <div className="bg-white rounded-3xl shadow-xl border border-neutral-200 p-8 md:p-12" data-id="element-1112">
          <Link to="/" className="flex items-center gap-2 mb-10 justify-center" data-id="element-1113">
            <div className="w-10 h-10 rounded bg-primary-900 flex items-center justify-center font-serif font-bold text-2xl text-secondary" data-id="element-1114">
              A
            </div>
            <span className="font-serif font-bold text-3xl tracking-tight text-primary-900" data-id="element-1115">
              AMU
            </span>
          </Link>

          <div className="mb-10 text-center" data-id="element-1116">
            <h1 className="text-3xl font-serif font-bold text-primary-900 mb-2" data-id="element-1117">
              Create an Account
            </h1>
            <p className="text-text-light" data-id="element-1118">
              Join Abuja's trusted real estate marketplace.
            </p>
          </div>

          {/* Account Type Tabs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-1 p-1 bg-neutral-100 rounded-xl mb-6" data-id="element-1119">
            {tabs.map(t => <button key={t.id} onClick={() => setAccountType(t.id)} className={`py-2.5 text-xs md:text-sm font-bold rounded-lg transition-all ${accountType === t.id ? 'bg-white text-primary-900 shadow-sm' : 'text-text-light hover:text-text'}`} data-id="element-1120">
                {t.label}
              </button>)}
          </div>

          {/* Contextual Notice per account type */}
          {accountType === 'poa' && <div className="mb-6 p-4 bg-secondary/5 border border-secondary/20 rounded-xl flex items-start gap-3" data-id="element-1121">
              <FileSignature className="text-secondary shrink-0 mt-0.5" size={18} data-id="element-1122" />
              <p className="text-xs text-text leading-relaxed" data-id="element-1123">
                <strong data-id="element-1124">Authorized Seller (Power of Attorney).</strong> You'll
                be asked to upload a valid POA document and proof of identity
                during listing verification.
              </p>
            </div>}
          {accountType === 'company' && <div className="mb-6 p-4 bg-primary-50 border border-primary-100 rounded-xl flex items-start gap-3" data-id="element-1125">
              <Building2 className="text-primary-900 shrink-0 mt-0.5" size={18} data-id="element-1126" />
              <p className="text-xs text-primary-900 leading-relaxed" data-id="element-1127">
                <strong data-id="element-1128">Real Estate Company.</strong> Reduced listing fees for
                multiple properties within the same estate.
              </p>
            </div>}

          <form onSubmit={handleRegister} className="space-y-5" data-id="element-1129">
            {/* Buyer kind toggle */}
            {accountType === 'buyer' && <div data-id="element-1130">
                <label className="block text-sm font-bold text-text mb-2" data-id="element-1131">
                  Buyer Type
                </label>
                <div className="grid grid-cols-2 gap-2" data-id="element-1132">
                  <button type="button" onClick={() => setBuyerKind('individual')} className={`flex items-center justify-center gap-2 py-3 rounded-xl border-2 text-sm font-bold transition-colors ${buyerKind === 'individual' ? 'border-primary-900 bg-primary-50 text-primary-900' : 'border-neutral-200 text-text-light hover:border-neutral-300'}`} data-id="element-1133">
                    <User size={16} data-id="element-1134" /> Individual
                  </button>
                  <button type="button" onClick={() => setBuyerKind('corporate')} className={`flex items-center justify-center gap-2 py-3 rounded-xl border-2 text-sm font-bold transition-colors ${buyerKind === 'corporate' ? 'border-primary-900 bg-primary-50 text-primary-900' : 'border-neutral-200 text-text-light hover:border-neutral-300'}`} data-id="element-1135">
                    <Briefcase size={16} data-id="element-1136" /> Corporate / Institutional
                  </button>
                </div>
              </div>}

            {/* Company / Corporate buyer / POA name field */}
            {(accountType === 'company' || accountType === 'buyer' && buyerKind === 'corporate') && <div data-id="element-1137">
                <label className="block text-sm font-bold text-text mb-2" data-id="element-1138">
                  {accountType === 'company' ? 'Company Name' : 'Organisation Name'}
                </label>
                <div className="relative" data-id="element-1139">
                  <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-dark" size={20} data-id="element-1140" />
                  <input type="text" placeholder={accountType === 'company' ? 'e.g. Premium Homes Ltd' : 'e.g. Abuja Investment Trust'} className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-neutral-200 bg-neutral-50 focus:bg-white focus:ring-2 focus:ring-primary-900 outline-none" required data-id="element-1141" />
                </div>
              </div>}

            {accountType === 'poa' && <div data-id="element-1142">
                <label className="block text-sm font-bold text-text mb-2" data-id="element-1143">
                  Owner You Represent
                </label>
                <div className="relative" data-id="element-1144">
                  <FileSignature className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-dark" size={20} data-id="element-1145" />
                  <input type="text" placeholder="Full legal name of the property owner" className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-neutral-200 bg-neutral-50 focus:bg-white focus:ring-2 focus:ring-primary-900 outline-none" required data-id="element-1146" />
                </div>
              </div>}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-1147">
              <div data-id="element-1148">
                <label className="block text-sm font-bold text-text mb-2" data-id="element-1149">
                  Full Name
                </label>
                <div className="relative" data-id="element-1150">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-dark" size={20} data-id="element-1151" />
                  <input type="text" placeholder="John Doe" className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-neutral-200 bg-neutral-50 focus:bg-white focus:ring-2 focus:ring-primary-900 outline-none" required data-id="element-1152" />
                </div>
              </div>
              <div data-id="element-1153">
                <label className="block text-sm font-bold text-text mb-2" data-id="element-1154">
                  Phone Number
                </label>
                <div className="relative" data-id="element-1155">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-dark" size={20} data-id="element-1156" />
                  <input type="tel" placeholder="+234" className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-neutral-200 bg-neutral-50 focus:bg-white focus:ring-2 focus:ring-primary-900 outline-none" required data-id="element-1157" />
                </div>
              </div>
            </div>

            <div data-id="element-1158">
              <label className="block text-sm font-bold text-text mb-2" data-id="element-1159">
                Email Address
              </label>
              <div className="relative" data-id="element-1160">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-dark" size={20} data-id="element-1161" />
                <input type="email" placeholder="name@example.com" className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-neutral-200 bg-neutral-50 focus:bg-white focus:ring-2 focus:ring-primary-900 outline-none" required data-id="element-1162" />
              </div>
            </div>

            <div data-id="element-1163">
              <label className="block text-sm font-bold text-text mb-2" data-id="element-1164">
                Password
              </label>
              <div className="relative" data-id="element-1165">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-dark" size={20} data-id="element-1166" />
                <input type="password" placeholder="••••••••" className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-neutral-200 bg-neutral-50 focus:bg-white focus:ring-2 focus:ring-primary-900 outline-none" required data-id="element-1167" />
              </div>
              <div className="flex gap-1 mt-2" data-id="element-1168">
                <div className="h-1.5 flex-1 bg-success rounded-full" data-id="element-1169"></div>
                <div className="h-1.5 flex-1 bg-success rounded-full" data-id="element-1170"></div>
                <div className="h-1.5 flex-1 bg-neutral-200 rounded-full" data-id="element-1171"></div>
                <div className="h-1.5 flex-1 bg-neutral-200 rounded-full" data-id="element-1172"></div>
              </div>
              <p className="text-xs text-text-light mt-1" data-id="element-1173">
                Must be at least 8 characters
              </p>
            </div>

            <div className="flex items-start mt-4" data-id="element-1174">
              <input type="checkbox" id="terms" className="mt-1 rounded text-primary-900 focus:ring-primary-900 w-4 h-4" required data-id="element-1175" />
              <label htmlFor="terms" className="ml-2 text-sm text-text-light leading-relaxed" data-id="element-1176">
                I agree to AMU's{' '}
                <a href="#" className="text-primary-900 font-bold" data-id="element-1177">
                  Terms of Service
                </a>
                ,{' '}
                <a href="#" className="text-primary-900 font-bold" data-id="element-1178">
                  Privacy Policy
                </a>
                , and consent to the verification of my identity and documents.
              </label>
            </div>

            <button type="submit" className="w-full bg-primary-900 hover:bg-primary-800 text-white py-4 rounded-xl font-bold transition-colors flex items-center justify-center gap-2 shadow-lg mt-6" data-id="element-1179">
              Create Account <ArrowRight size={18} data-id="element-1180" />
            </button>
          </form>

          <p className="mt-8 text-center text-sm text-text-light" data-id="element-1181">
            Already have an account?{' '}
            <Link to="/login" className="font-bold text-primary-900 hover:text-secondary transition-colors" data-id="element-1182">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>;
};